/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import {
  Sparkle, Certificate, Truck, ShieldCheck, ArrowRight, MoonStars,
} from "@phosphor-icons/react";

const CORAL_VIDEO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_astro-connect-42/artifacts/xm3a7ci8_c93fca5a-e757-4bb5-99ae-087cf74cb050.MP4";

const PRODUCTS = [
  {
    slug: "red-coral-6-21ct",
    name: "Red Coral (Moonga)",
    weight: "6.21 ct",
    origin: "Italy",
    price: 14640,
    dims: "17.83 × 6.26 × 5.83 mm",
    media: CORAL_VIDEO_URL,
    mediaType: "video",
    ruling: "Mars (Mangal)",
    highlight: "Italian · Government-Certified · 1 piece only",
  },
];

export default function StoreIndexPage() {
  return (
    <PageShell>
      <SEO
        title="AstroKuberChat Store — Government-Certified Vedic Gemstones"
        description="Shop hand-picked, government-certified Vedic gemstones — Italian Red Coral, Yellow Sapphire, Ruby, Emerald and more. Every stone is 100% natural, lab-tested and ships with its certificate."
        path="/store"
        keywords={[
          "vedic gemstone store", "government certified gemstones",
          "buy red coral online", "italian moonga", "astrology gemstones",
          "certified vedic gemstones india",
        ]}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-24" data-testid="store-index-page">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
            <MoonStars size={12} weight="fill" /> AstroKuberChat Store
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 font-light tracking-tight leading-[1.1]"
              data-testid="store-heading">
            Government-certified <span className="text-gold">Vedic gemstones</span>,
            hand-picked for your chart
          </h1>
          <p className="text-muted mt-5 text-sm sm:text-base max-w-2xl mx-auto font-light leading-relaxed">
            Every stone at the AstroKuberChat Store is 100% natural, unheated
            and untreated — tested and certified by a government-approved
            gemological lab before it reaches you. No hidden tiers, no
            add-on certification fees.
          </p>
        </div>

        {/* Promise strip */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-10 max-w-3xl mx-auto">
          <PromiseCard icon={<Certificate size={20} weight="duotone" className="text-gold" />}
                       title="Govt.-lab certified" body="Included in the price. No upsell." />
          <PromiseCard icon={<Truck size={20} weight="duotone" className="text-gold" />}
                       title="Insured shipping" body="Real-time tracking · 3–5 days" />
          <PromiseCard icon={<ShieldCheck size={20} weight="duotone" className="text-gold" />}
                       title="7-day return" body="No questions asked" />
        </div>

        {/* Product grid */}
        <div className="mt-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-gold label-eyebrow flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> Featured Stones
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light">
                In stock now
              </h2>
            </div>
            <div className="text-xs text-muted">Every piece — one of a kind</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-product-grid">
            {PRODUCTS.map((p) => <ProductCard key={p.slug} p={p} />)}
            <ComingSoonCard label="Yellow Sapphire · Coming soon" />
            <ComingSoonCard label="Ruby · Coming soon" />
          </div>
        </div>

        {/* Trust callout */}
        <div className="mt-16 sm:mt-20 card-surface rounded-2xl border border-gold/40 p-6 sm:p-10 text-center">
          <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
            <Sparkle size={12} weight="fill" /> Not sure which stone to buy?
          </div>
          <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light">
            Chat free with our Vedic AI astrologers first
          </h3>
          <p className="text-muted text-sm mt-2 max-w-xl mx-auto font-light">
            Share your birth details, get a Mars-placement check in 3 minutes
            (free), then buy the stone that actually fits your chart.
          </p>
          <Link
            to="/astrologers"
            className="btn-primary rounded-full px-6 py-3 mt-5 inline-flex items-center gap-2 text-sm font-medium"
            data-testid="store-consult-cta"
          >
            <Sparkle size={14} weight="fill" />
            Consult before buying — free 3 min
          </Link>
        </div>
      </div>
    </PageShell>
  );
}

function PromiseCard({ icon, title, body }) {
  return (
    <div className="card-surface rounded-md p-4 text-center border border-soft">
      <div className="flex justify-center">{icon}</div>
      <div className="text-xs sm:text-sm font-medium text-cream mt-2">{title}</div>
      <div className="text-[10px] sm:text-xs text-muted mt-0.5">{body}</div>
    </div>
  );
}

function ProductCard({ p }) {
  return (
    <Link
      to={`/store/${p.slug}`}
      className="group card-surface rounded-2xl overflow-hidden border border-soft hover:border-gold/50 hover:-translate-y-0.5 transition-all"
      data-testid={`store-card-${p.slug}`}
    >
      <div className="relative bg-[#1a0f2e]" style={{ aspectRatio: "1 / 1" }}>
        {p.mediaType === "video" ? (
          <video
            src={p.media} autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
          />
        ) : (
          <img src={p.media} alt={p.name} loading="lazy"
               className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] label-eyebrow bg-bg/80 border border-soft rounded-full px-2.5 py-1">
          <Certificate size={10} weight="fill" /> Govt.-Certified
        </div>
      </div>
      <div className="p-5">
        <div className="text-xs text-muted">{p.origin} · Ruling planet: {p.ruling}</div>
        <div className="font-serif-display text-2xl mt-1 leading-tight">
          {p.name}
        </div>
        <div className="text-sm text-cream mt-1">{p.weight} · {p.dims}</div>
        <div className="text-[10px] text-gold mt-2 uppercase tracking-widest">{p.highlight}</div>
        <div className="mt-4 flex items-center justify-between">
          <div className="font-serif-display text-2xl text-gold">
            ₹{p.price.toLocaleString("en-IN")}
          </div>
          <div className="text-xs text-gold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
            View details <ArrowRight size={12} weight="bold" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ComingSoonCard({ label }) {
  return (
    <div className="card-surface rounded-2xl border border-dashed border-soft flex flex-col items-center justify-center text-center py-16 opacity-70">
      <MoonStars size={26} weight="duotone" className="text-gold/60" />
      <div className="text-xs text-muted mt-3 tracking-wide">{label}</div>
    </div>
  );
}
